/**
 * Maps MCP tool function names to connector metadata for ToolCallIndicator.
 * Uses the same icons as ConnectYourToolsBar / ManageConnectorsTab.
 */

import React from 'react';
import { Terminal } from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { SiNotion } from 'react-icons/si';

import gmailSvg from '../../../assets/gmail.svg';
import googleCalendarSvg from '../../../assets/google-calendar.svg';
import driveSvg from '../../../assets/drive.svg';
import outlookSvg from '../../../assets/outlook.svg';
import outlookCalendarSvg from '../../../assets/outlook-calendar.svg';
import sharepointSvg from '../../../assets/sharepoint.svg';
import onedriveSvg from '../../../assets/onedrive.svg';

const HubSpotIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 489 511.8" className="w-full h-full" {...props}>
    <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04zm0 0z" />
  </svg>
);

const createImgIcon = (src) => ({ size = 16, className, ...props }) => (
  <img src={src} alt="" width={size} height={size} className={`object-contain ${className || ''}`} {...props} />
);

const GmailIcon = createImgIcon(gmailSvg);
const GoogleCalendarIcon = createImgIcon(googleCalendarSvg);
const DriveIcon = createImgIcon(driveSvg);
const OutlookIcon = createImgIcon(outlookSvg);
const OutlookCalendarIcon = createImgIcon(outlookCalendarSvg);
const SharePointIcon = createImgIcon(sharepointSvg);
const OneDriveIcon = createImgIcon(onedriveSvg);

function tool(_name, appName, connectorId, Icon, inProgress, complete) {
  return {
    connectorId,
    appName,
    Icon,
    getInProgressStatus: () => inProgress,
    getCompleteStatus: (success) => (success ? complete : 'Failed'),
  };
}

function hubspotTool(name, inProgress) {
  return {
    connectorId: 'hubspot',
    appName: 'HubSpot',
    Icon: HubSpotIcon,
    getInProgressStatus: () => inProgress,
    getCompleteStatus: (success, recordCount = 0) =>
      success ? `Found ${recordCount} record${recordCount === 1 ? '' : 's'}` : 'Failed',
  };
}

const TOOL_MAP = {
  // Outlook Calendar
  outlook_calendar_create_event: tool('outlook_calendar_create_event', 'Outlook Calendar', 'outlook-calendar', OutlookCalendarIcon, 'Creating the event...', 'Event created'),
  outlook_calendar_update_event: tool('outlook_calendar_update_event', 'Outlook Calendar', 'outlook-calendar', OutlookCalendarIcon, 'Updating the event...', 'Event updated'),
  outlook_calendar_delete_event: tool('outlook_calendar_delete_event', 'Outlook Calendar', 'outlook-calendar', OutlookCalendarIcon, 'Deleting the event...', 'Event deleted'),
  outlook_calendar_list_events: tool('outlook_calendar_list_events', 'Outlook Calendar', 'outlook-calendar', OutlookCalendarIcon, 'Checking your calendar...', 'Calendar checked'),
  outlook_calendar_find_free_slots: tool('outlook_calendar_find_free_slots', 'Outlook Calendar', 'outlook-calendar', OutlookCalendarIcon, 'Finding available times...', 'Free slots found'),

  // Outlook Mail
  outlook_mail_send: tool('outlook_mail_send', 'Outlook Mail', 'outlook-mail', OutlookIcon, 'Sending email...', 'Email sent'),
  outlook_mail_list: tool('outlook_mail_list', 'Outlook Mail', 'outlook-mail', OutlookIcon, 'Searching your inbox...', 'Emails found'),
  outlook_mail_read: tool('outlook_mail_read', 'Outlook Mail', 'outlook-mail', OutlookIcon, 'Reading email...', 'Email read'),
  outlook_mail_draft: tool('outlook_mail_draft', 'Outlook Mail', 'outlook-mail', OutlookIcon, 'Drafting email...', 'Draft created'),

  // Gmail
  gmail_send: tool('gmail_send', 'Gmail', 'gmail', GmailIcon, 'Sending email...', 'Email sent'),
  gmail_list: tool('gmail_list', 'Gmail', 'gmail', GmailIcon, 'Searching your inbox...', 'Emails found'),
  gmail_read: tool('gmail_read', 'Gmail', 'gmail', GmailIcon, 'Reading email...', 'Email read'),
  gmail_draft: tool('gmail_draft', 'Gmail', 'gmail', GmailIcon, 'Drafting email...', 'Draft created'),

  // Google Calendar
  google_calendar_create_event: tool('google_calendar_create_event', 'Google Calendar', 'google-calendar', GoogleCalendarIcon, 'Creating the event...', 'Event created'),
  google_calendar_list_events: tool('google_calendar_list_events', 'Google Calendar', 'google-calendar', GoogleCalendarIcon, 'Checking your calendar...', 'Calendar checked'),
  google_calendar_update_event: tool('google_calendar_update_event', 'Google Calendar', 'google-calendar', GoogleCalendarIcon, 'Updating the event...', 'Event updated'),
  google_calendar_delete_event: tool('google_calendar_delete_event', 'Google Calendar', 'google-calendar', GoogleCalendarIcon, 'Deleting the event...', 'Event deleted'),
  google_calendar_find_free_slots: tool('google_calendar_find_free_slots', 'Google Calendar', 'google-calendar', GoogleCalendarIcon, 'Finding available times...', 'Free slots found'),

  // Google Drive
  google_drive_search: tool('google_drive_search', 'Google Drive', 'google-drive', DriveIcon, 'Searching your Drive...', 'Search complete'),
  google_drive_list: tool('google_drive_list', 'Google Drive', 'google-drive', DriveIcon, 'Listing files...', 'Files listed'),
  google_drive_read: tool('google_drive_read', 'Google Drive', 'google-drive', DriveIcon, 'Reading file...', 'File read'),

  // SharePoint
  sharepoint_search: tool('sharepoint_search', 'SharePoint', 'sharepoint', SharePointIcon, 'Searching SharePoint...', 'Search complete'),
  sharepoint_list: tool('sharepoint_list', 'SharePoint', 'sharepoint', SharePointIcon, 'Listing files...', 'Files listed'),

  // OneDrive
  onedrive_search: tool('onedrive_search', 'OneDrive', 'onedrive', OneDriveIcon, 'Searching OneDrive...', 'Search complete'),
  onedrive_list: tool('onedrive_list', 'OneDrive', 'onedrive', OneDriveIcon, 'Listing files...', 'Files listed'),

  // GitHub
  github_list_repos: tool('github_list_repos', 'GitHub', 'github', FaGithub, 'Listing repositories...', 'Repos listed'),
  github_list_open_prs: tool('github_list_open_prs', 'GitHub', 'github', FaGithub, 'Fetching open PRs...', 'PRs fetched'),
  github_search_issues: tool('github_search_issues', 'GitHub', 'github', FaGithub, 'Searching issues...', 'Issues searched'),
  github_search_code: tool('github_search_code', 'GitHub', 'github', FaGithub, 'Searching code...', 'Code searched'),
  github_read_file: tool('github_read_file', 'GitHub', 'github', FaGithub, 'Reading file...', 'File read'),
  github_create_issue: tool('github_create_issue', 'GitHub', 'github', FaGithub, 'Creating issue...', 'Issue created'),
  github_create_pull_request: tool('github_create_pull_request', 'GitHub', 'github', FaGithub, 'Creating PR...', 'PR created'),

  // Notion
  notion_search: tool('notion_search', 'Notion', 'notion', SiNotion, 'Searching Notion...', 'Search complete'),
  notion_read: tool('notion_read', 'Notion', 'notion', SiNotion, 'Reading page...', 'Page read'),

  // File generation (chart, docs, artifacts, etc.) - terminal-style indicator
  create_html_artifact: tool('create_html_artifact', 'Artifact', 'file-generation', Terminal, 'Building artifact...', 'Created artifact'),
  generate_html_chart: tool('generate_html_chart', 'Chart', 'file-generation', Terminal, 'Creating chart...', 'Created HTML chart'),
  generate_word_document: tool('generate_word_document', 'Word', 'file-generation', Terminal, 'Creating document...', 'Created Word document'),
  generate_excel_spreadsheet: tool('generate_excel_spreadsheet', 'Excel', 'file-generation', Terminal, 'Creating spreadsheet...', 'Created Excel file'),
  generate_csv_file: tool('generate_csv_file', 'CSV', 'file-generation', Terminal, 'Creating CSV...', 'Created CSV file'),
  generate_pdf_document: tool('generate_pdf_document', 'PDF', 'file-generation', Terminal, 'Creating PDF...', 'Created PDF'),
  generate_powerpoint_from_code: tool('generate_powerpoint_from_code', 'PowerPoint', 'file-generation', Terminal, 'Creating presentation...', 'Created PowerPoint'),
  generate_powerpoint_presentation: tool('generate_powerpoint_presentation', 'PowerPoint', 'file-generation', Terminal, 'Creating presentation...', 'Created PowerPoint'),
};

// HubSpot MCP tools from mcp.hubspot.com - bare names, no hubspot_ prefix
const HUBSPOT_MCP_TOOL_NAMES = new Set(['search_crm_objects', 'get_crm_objects', 'get_properties', 'search_owners', 'search_properties', 'get_user_details']);

const DEFAULT_META = {
  connectorId: 'unknown',
  appName: 'Tool',
  Icon: Terminal,
  getInProgressStatus: () => 'Working...',
  getCompleteStatus: (success) => (success ? 'Done' : 'Failed'),
};

/**
 * Get metadata for a tool by function name.
 * Falls back to prefix matching for tools like outlook_calendar_*, hubspot_*, etc.
 */
export function getToolCallMeta(functionName, options = {}) {
  const { success = true, recordCount } = options;
  if (!functionName) return DEFAULT_META;

  const exact = TOOL_MAP[functionName];
  if (exact) return exact;

  // HubSpot: prefix (hubspot_*, hubspot-*) or bare MCP names (search_owners, get_user_details, etc.)
  if (
    functionName.startsWith('hubspot_mcp_') ||
    functionName.startsWith('hubspot_remote_') ||
    functionName.startsWith('hubspot-') ||
    functionName.startsWith('hubspot_') ||
    HUBSPOT_MCP_TOOL_NAMES.has(functionName)
  ) {
    return hubspotTool(functionName, 'Accessing HubSpot');
  }

  if (functionName.startsWith('outlook_calendar_')) {
    return tool(functionName, 'Outlook Calendar', 'outlook-calendar', OutlookCalendarIcon, 'Working with Outlook Calendar...', 'Calendar updated');
  }
  if (functionName.startsWith('outlook_mail_')) {
    return tool(functionName, 'Outlook Mail', 'outlook-mail', OutlookIcon, 'Working with Outlook...', 'Done');
  }
  if (functionName.startsWith('gmail_')) {
    return tool(functionName, 'Gmail', 'gmail', GmailIcon, 'Working with Gmail...', 'Done');
  }
  if (functionName.startsWith('google_calendar_')) {
    return tool(functionName, 'Google Calendar', 'google-calendar', GoogleCalendarIcon, 'Working with Google Calendar...', 'Calendar updated');
  }
  if (functionName.startsWith('google_drive_')) {
    return tool(functionName, 'Google Drive', 'google-drive', DriveIcon, 'Working with Google Drive...', 'Done');
  }
  if (functionName.startsWith('sharepoint_')) {
    return tool(functionName, 'SharePoint', 'sharepoint', SharePointIcon, 'Working with SharePoint...', 'Done');
  }
  if (functionName.startsWith('onedrive_')) {
    return tool(functionName, 'OneDrive', 'onedrive', OneDriveIcon, 'Working with OneDrive...', 'Done');
  }
  if (functionName.startsWith('github_')) {
    return tool(functionName, 'GitHub', 'github', FaGithub, 'Working with GitHub...', 'Complete');
  }
  if (functionName.startsWith('notion_')) {
    return tool(functionName, 'Notion', 'notion', SiNotion, 'Working with Notion...', 'Complete');
  }

  return DEFAULT_META;
}

/**
 * Get display status for a completed tool call.
 */
export function getCompletedToolStatus(functionName, backendStatus, success = true, recordCount) {
  if (backendStatus && backendStatus.trim()) return backendStatus;
  const meta = getToolCallMeta(functionName, { success, recordCount });
  return meta.getCompleteStatus(success, recordCount);
}
